import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '@ibk/shared-translation';
import { Button, Icon } from '@ibk/shared-ui';
import styles from './BenefitsOnboardingSpotlight.module.css';
import { BENEFITS_ONBOARDING_SPOTLIGHT_DEFAULT_TEST_ID } from './benefitsOnboardingTestIds';
import { BENEFITS_ONBOARDING_TOUR_PANEL_ARROW_LEFT_INSET, computeTourArrowTargetX, } from './benefitsOnboardingTourArrowLayout';
import { computeTourPanelLeftPx, } from './benefitsOnboardingTourPanelLayout';
const SPOTLIGHT_PADDING_INSET = 8;
function spotlightPaddingForStep(stepIndex) {
    return stepIndex === 1 || stepIndex === 2 ? SPOTLIGHT_PADDING_INSET : 0;
}
const OVERLAY_Z = 99990;
const HOLE_BORDER_RADIUS_PX = 4;
const PANEL_GAP = 12;
const ARROW_BASE_HALF = 10;
const ARROW_EDGE_INSET = 8;
function readTargetRect(selector, resolveElement) {
    const fromResolver = resolveElement?.() ?? null;
    let el = null;
    if (fromResolver?.isConnected) {
        el = fromResolver;
    }
    else if (selector) {
        el = document.querySelector(selector);
    }
    if (!el || !(el instanceof HTMLElement)) {
        return null;
    }
    const r = el.getBoundingClientRect();
    if (r.width < 2 && r.height < 2) {
        return null;
    }
    return { top: r.top, left: r.left, width: r.width, height: r.height };
}
function isScrollableElement(el) {
    if (!(el instanceof HTMLElement)) {
        return false;
    }
    if (el === document.documentElement) {
        return el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;
    }
    const s = window.getComputedStyle(el);
    const y = s.overflowY;
    const x = s.overflowX;
    const yScroll = (y === 'auto' || y === 'scroll' || y === 'overlay') && el.scrollHeight > el.clientHeight;
    const xScroll = (x === 'auto' || x === 'scroll' || x === 'overlay') && el.scrollWidth > el.clientWidth;
    return Boolean(yScroll || xScroll);
}
function collectScrollableAncestors(node) {
    if (!node) {
        return [];
    }
    const out = [];
    let el = node;
    while (el) {
        if (isScrollableElement(el)) {
            out.push(el);
        }
        if (el === document.documentElement) {
            break;
        }
        el = el.parentElement;
    }
    return out;
}
function getLayoutViewportSize() {
    if (typeof document === 'undefined' || !document.documentElement) {
        return { w: 1280, h: 800 };
    }
    return {
        w: document.documentElement.clientWidth,
        h: document.documentElement.clientHeight,
    };
}
function tryPanelChromeLeading(p) {
    if (p.panelPlacement !== 'leading') {
        return null;
    }
    const { tTop, tLeft, tH, vh, panelH, margin, panelW, panelLeadingVerticalAlign } = p;
    let leftLeading = tLeft - PANEL_GAP - panelW;
    leftLeading = Math.max(margin, leftLeading);
    if (leftLeading + panelW + PANEL_GAP > tLeft) {
        return null;
    }
    let topLeading = panelLeadingVerticalAlign === 'start' ? tTop : tTop + tH / 2 - panelH / 2;
    topLeading = Math.max(margin, Math.min(topLeading, vh - margin - panelH));
    return {
        top: topLeading,
        left: leftLeading,
        panelAboveHole: false,
        arrowOffsetX: 0,
        placement: 'leading',
        leadingArrowTopPx: null,
    };
}
function tryPanelChromeTrailing(p) {
    if (p.panelPlacement !== 'trailing') {
        return null;
    }
    const { tTop, tLeft, tW, tH, vh, panelH, margin, panelW, vw, panelLeadingVerticalAlign } = p;
    const idealLeft = tLeft + tW + PANEL_GAP;
    let leftTrailing = idealLeft;
    if (leftTrailing + panelW > vw - margin) {
        leftTrailing = vw - margin - panelW;
    }
    leftTrailing = Math.max(margin, leftTrailing);
    if (leftTrailing < idealLeft - 1) {
        return null;
    }
    let topTrailing = panelLeadingVerticalAlign === 'start' ? tTop : tTop + tH / 2 - panelH / 2;
    topTrailing = Math.max(margin, Math.min(topTrailing, vh - margin - panelH));
    return {
        top: topTrailing,
        left: leftTrailing,
        panelAboveHole: false,
        arrowOffsetX: 0,
        placement: 'trailing',
        leadingArrowTopPx: null,
    };
}
function buildPanelChromeStack(p) {
    const { tTop, tLeft, tW, tH, vh, panelH, panelW, vw, margin, panelHorizontalAlign, arrowXMode } = p;
    const bottomY = tTop + tH + PANEL_GAP;
    let top = bottomY;
    let panelAboveHole = false;
    if (top + panelH > vh - 16) {
        top = tTop - PANEL_GAP - panelH;
        panelAboveHole = true;
    }
    if (top < 16) {
        top = 16;
    }
    let left = computeTourPanelLeftPx({
        horizontal: panelHorizontalAlign,
        targetLeft: tLeft,
        targetWidth: tW,
        panelWidth: panelW,
        viewportWidth: vw,
        margin,
    });
    const arrowMin = ARROW_BASE_HALF + ARROW_EDGE_INSET;
    const arrowMax = panelW - ARROW_BASE_HALF - ARROW_EDGE_INSET;
    const holeArrowTargetX = arrowXMode === 'panel-start'
        ? null
        : computeTourArrowTargetX({
            mode: arrowXMode,
            holeLeft: tLeft,
            holeWidth: tW,
        });
    if (holeArrowTargetX !== null) {
        const nudgedLeft = Math.min(Math.max(left, holeArrowTargetX - arrowMax), holeArrowTargetX - arrowMin);
        left = Math.max(margin, Math.min(nudgedLeft, vw - margin - panelW));
    }
    const arrowTargetX = holeArrowTargetX ?? left + BENEFITS_ONBOARDING_TOUR_PANEL_ARROW_LEFT_INSET;
    const arrowOffsetX = Math.max(arrowMin, Math.min(arrowMax, arrowTargetX - left));
    return {
        top,
        left,
        panelAboveHole,
        arrowOffsetX,
        placement: 'stack',
        leadingArrowTopPx: null,
    };
}
export function BenefitsOnboardingSpotlight({ stepIndex, totalSteps, targetSelector, resolveTargetElement, extraHighlightSelector, title, body, onBack, onForward, forwardLabel, backDisabled = false, onRequestClose, panelHorizontalAlign = 'center', arrowXMode = 'hole-center', panelPlacement = 'stack', panelMaxWidthPx = 297, panelLeadingVerticalAlign = 'center', 'data-testid': testId = BENEFITS_ONBOARDING_SPOTLIGHT_DEFAULT_TEST_ID, }) {
    const { t } = useTranslation();
    const [rect, setRect] = useState(null);
    const [extraRect, setExtraRect] = useState(null);
    const [panelChrome, setPanelChrome] = useState({
        top: 0,
        left: 0,
        panelAboveHole: false,
        arrowOffsetX: 0,
        placement: 'stack',
        leadingArrowTopPx: null,
    });
    const [viewport, setViewport] = useState(() => getLayoutViewportSize());
    const panelRef = useRef(null);
    const displayStep = stepIndex + 1;
    const spotlightMaskId = useId().replace(/:/g, '-');
    const stepSummary = t('benefits.onboarding.tour.stepSummary', { current: displayStep, total: totalSteps });
    const updateRect = useCallback(() => {
        setViewport(getLayoutViewportSize());
        setRect(readTargetRect(targetSelector, resolveTargetElement));
        setExtraRect(extraHighlightSelector ? readTargetRect(extraHighlightSelector) : null);
    }, [targetSelector, resolveTargetElement, extraHighlightSelector]);
    const updatePanelPosition = useCallback(() => {
        if (!rect || !panelRef.current) {
            return;
        }
        const panelH = panelRef.current.offsetHeight;
        const { w: layoutW, h: layoutH } = getLayoutViewportSize();
        const panelW = Math.min(panelMaxWidthPx, layoutW - 32);
        const vw = layoutW;
        const vh = layoutH;
        const margin = 16;
        const pad = spotlightPaddingForStep(stepIndex);
        const tTop = Math.max(0, rect.top - pad);
        const tLeft = Math.max(0, rect.left - pad);
        const tW = rect.width + pad * 2;
        const tH = rect.height + pad * 2;
        const posBase = {
            panelH,
            panelW,
            vw,
            vh,
            tTop,
            tLeft,
            tW,
            tH,
            margin,
            panelHorizontalAlign,
            arrowXMode,
            panelPlacement,
            panelLeadingVerticalAlign,
        };
        const fromLeading = tryPanelChromeLeading(posBase);
        if (fromLeading) {
            setPanelChrome(fromLeading);
            return;
        }
        const fromTrailing = tryPanelChromeTrailing(posBase);
        if (fromTrailing) {
            setPanelChrome(fromTrailing);
            return;
        }
        setPanelChrome(buildPanelChromeStack(posBase));
    }, [rect, panelHorizontalAlign, arrowXMode, panelPlacement, panelLeadingVerticalAlign, panelMaxWidthPx, stepIndex]);
    useLayoutEffect(() => {
        updateRect();
        const onResize = () => {
            updateRect();
        };
        const onScroll = () => {
            updateRect();
        };
        const layoutResizeRo = new ResizeObserver(() => {
            updateRect();
        });
        layoutResizeRo.observe(document.documentElement);
        window.addEventListener('resize', onResize);
        window.addEventListener('scroll', onScroll, true);
        const scrollableCleanups = [];
        const getTargetEl = () => {
            const fromR = resolveTargetElement?.() ?? null;
            if (fromR?.isConnected) {
                return fromR;
            }
            if (targetSelector) {
                const q = document.querySelector(targetSelector);
                return q instanceof HTMLElement ? q : null;
            }
            return null;
        };
        const rebindScrollableScrollListeners = () => {
            while (scrollableCleanups.length > 0) {
                scrollableCleanups.pop()?.();
            }
            const forEl = getTargetEl();
            if (!forEl) {
                return;
            }
            for (const host of collectScrollableAncestors(forEl)) {
                const scrollHost = host;
                scrollHost.addEventListener('scroll', onScroll, { passive: true, capture: true });
                scrollableCleanups.push(() => {
                    scrollHost.removeEventListener('scroll', onScroll, { capture: true });
                });
            }
        };
        let ro = null;
        const attachObserver = () => {
            rebindScrollableScrollListeners();
            ro?.disconnect();
            const el = getTargetEl();
            if (!(el instanceof HTMLElement)) {
                return;
            }
            ro = new ResizeObserver(() => {
                updateRect();
            });
            ro.observe(el);
        };
        attachObserver();
        const visualViewport = window.visualViewport;
        if (visualViewport) {
            visualViewport.addEventListener('scroll', onScroll);
            visualViewport.addEventListener('resize', onResize);
        }
        let rafId = 0;
        if (resolveTargetElement) {
            const el0 = getTargetEl();
            const needsPoll = !el0 || el0.getBoundingClientRect().width < 2;
            if (needsPoll) {
                let frame = 0;
                const maxFrames = 32;
                const poll = () => {
                    updateRect();
                    attachObserver();
                    frame += 1;
                    const el = getTargetEl();
                    if (el && el.getBoundingClientRect().width > 1) {
                        return;
                    }
                    if (frame < maxFrames) {
                        rafId = requestAnimationFrame(poll);
                    }
                };
                rafId = requestAnimationFrame(poll);
            }
        }
        return () => {
            cancelAnimationFrame(rafId);
            layoutResizeRo.disconnect();
            window.removeEventListener('resize', onResize);
            window.removeEventListener('scroll', onScroll, true);
            while (scrollableCleanups.length > 0) {
                scrollableCleanups.pop()?.();
            }
            if (visualViewport) {
                visualViewport.removeEventListener('scroll', onScroll);
                visualViewport.removeEventListener('resize', onResize);
            }
            ro?.disconnect();
        };
    }, [targetSelector, resolveTargetElement, updateRect]);
    useLayoutEffect(() => {
        if (!rect) {
            return;
        }
        updatePanelPosition();
    }, [rect, body, title, displayStep, updatePanelPosition, forwardLabel, stepIndex]);
    useLayoutEffect(() => {
        if (!rect) {
            return;
        }
        const onResize = () => updatePanelPosition();
        const ro = panelRef.current ? new ResizeObserver(onResize) : null;
        if (panelRef.current && ro) {
            ro.observe(panelRef.current);
        }
        return () => ro?.disconnect();
    }, [rect, updatePanelPosition]);
    const hasHole = rect !== null;
    const holePad = spotlightPaddingForStep(stepIndex);
    const top = hasHole ? Math.max(0, rect.top - holePad) : 0;
    const left = hasHole ? Math.max(0, rect.left - holePad) : 0;
    const width = hasHole ? rect.width + holePad * 2 : 0;
    const height = hasHole ? rect.height + holePad * 2 : 0;
    const overlay = (_jsxs("div", { className: styles.root, style: { zIndex: OVERLAY_Z }, "data-testid": testId, children: [hasHole ? (_jsxs("svg", { className: styles.spotlightDim, "aria-hidden": true, viewBox: `0 0 ${viewport.w} ${viewport.h}`, preserveAspectRatio: 'none', width: '100%', height: '100%', style: { zIndex: OVERLAY_Z }, children: [_jsx("defs", { children: _jsxs("mask", { id: spotlightMaskId, maskUnits: 'userSpaceOnUse', x: 0, y: 0, width: viewport.w, height: viewport.h, children: [_jsx("rect", { x: 0, y: 0, width: viewport.w, height: viewport.h, fill: 'white' }), _jsx("rect", { x: left, y: top, width: width, height: height, rx: HOLE_BORDER_RADIUS_PX, ry: HOLE_BORDER_RADIUS_PX, fill: 'black' }), extraRect && (_jsx("rect", { x: extraRect.left, y: extraRect.top, width: extraRect.width, height: extraRect.height, rx: HOLE_BORDER_RADIUS_PX, ry: HOLE_BORDER_RADIUS_PX, fill: 'black', "data-testid": `${testId}-extra-hole` }))] }) }), _jsx("rect", { x: 0, y: 0, width: viewport.w, height: viewport.h, fill: 'rgba(15, 15, 15, 0.55)', mask: `url(#${spotlightMaskId})` })] })) : (_jsx("div", { className: styles.fullDim, style: { zIndex: OVERLAY_Z } })), _jsxs("div", { ref: panelRef, className: styles.panel, "data-testid": `${testId}-panel`, style: {
                    ['--benefits-onboarding-panel-max-px']: `${panelMaxWidthPx}px`,
                    ...(hasHole
                        ? { top: panelChrome.top, left: panelChrome.left, zIndex: 99991 }
                        : { bottom: 24, left: '50%', top: 'auto', zIndex: 99991, transform: 'translateX(-50%)' }),
                }, children: [hasHole && panelChrome.placement === 'stack' && (_jsx("span", { className: panelChrome.panelAboveHole ? styles.panelArrowDown : styles.panelArrowUp, style: { left: panelChrome.arrowOffsetX }, "data-testid": `${testId}-arrow`, "aria-hidden": true })), hasHole && panelChrome.placement === 'leading' && (_jsx("span", { className: styles.panelArrowRight, style: panelChrome.leadingArrowTopPx !== null ? { top: panelChrome.leadingArrowTopPx, marginTop: 0 } : undefined, "data-testid": `${testId}-arrow`, "aria-hidden": true })), hasHole && panelChrome.placement === 'trailing' && (_jsx("span", { className: styles.panelArrowLeft, "data-testid": `${testId}-arrow`, "aria-hidden": true })), _jsxs("div", { className: styles.panelHeader, children: [_jsx("button", { type: 'button', className: styles.closePanel, onClick: onRequestClose, "data-testid": `${testId}-close`, "aria-label": t('benefits.onboarding.tour.close-aria'), children: _jsx(Icon, { name: 'actionClose', className: styles.closePanelIcon, "aria-hidden": true }) }), _jsx("h2", { className: styles.panelTitle, title: title, children: title })] }), _jsx("div", { className: styles.scrollBody, children: _jsx("p", { className: styles.panelBody, children: body }) }), _jsxs("div", { className: styles.panelFooter, children: [_jsx("span", { className: styles.stepText, "data-testid": `${testId}-step-indicator`, children: stepSummary }), _jsxs("div", { className: styles.buttonGroup, children: [_jsx(Button, { variant: 'outline', size: 'small', onClick: onBack, disabled: backDisabled, dataTestId: `${testId}-back`, leftIcon: 'arrowLineLeft', children: t('benefits.onboarding.tour.back') }), _jsx(Button, { variant: 'primary', size: 'small', onClick: onForward, dataTestId: `${testId}-forward`, rightIcon: 'arrowLineRight', children: forwardLabel })] })] })] })] }));
    if (typeof document === 'undefined') {
        return null;
    }
    return createPortal(overlay, document.body);
}
